<template>
    <div
        class="collection-editor new-collection-page admin-weflix-new-collection weflix-admin-newCollection"
        data-aos="fade-left"
        data-aos-duration="1500"
    >
        <main class="collection-editor__container">
            <nav class="collection-editor__breadcrumb" aria-label="Trilha">
                <router-link to="/admin/colecoes">Coleções</router-link>
                <span class="collection-editor__breadcrumb-sep" aria-hidden="true">
                    <fa icon="fa-chevron-right" />
                </span>
                <span>Nova coleção</span>
            </nav>

            <header class="collection-editor__page-header">
                <div>
                    <h1 class="collection-editor__title">Nova Coleção</h1>
                    <p class="collection-editor__subtitle">
                        Defina as informações, configuração de venda e a capa da sua coleção de vídeos.
                    </p>
                </div>
                <div class="collection-editor__header-actions">
                    <button
                        type="button"
                        class="collection-editor__btn collection-editor__btn--ghost"
                        :disabled="fillingPreview || !loaderImage"
                        @click="fillPreviewData"
                    >
                        {{ fillingPreview ? 'Preenchendo...' : 'Preencher dados' }}
                    </button>
                </div>
            </header>

            <div class="collection-editor__grid">
                <div class="collection-editor__column">
                    <section class="collection-editor__card collection-editor__card--info">
                        <div class="collection-editor__card-header">
                            <span class="collection-editor__card-icon" aria-hidden="true">
                                <fa icon="fa-circle-info" />
                            </span>
                            <div>
                                <h2 class="collection-editor__card-title">Informações da coleção</h2>
                                <p class="collection-editor__card-description">
                                    Defina os detalhes principais da sua coleção.
                                </p>
                            </div>
                        </div>

                        <div class="collection-editor__field">
                            <div class="collection-editor__label-row">
                                <label class="collection-editor__label" for="new-collection-name">
                                    Nome da coleção
                                </label>
                                <v-tooltip text="Campo obrigatório" location="top">
                                    <template v-slot:activator="{ props }">
                                        <fa
                                            v-bind="props"
                                            class="collection-editor__help"
                                            icon="fa-solid fa-circle-exclamation"
                                        />
                                    </template>
                                </v-tooltip>
                            </div>
                            <input
                                id="new-collection-name"
                                type="text"
                                required
                                class="collection-editor__input"
                                v-model="name"
                                placeholder="Título da sua coleção"
                            />
                            <small class="collection-editor__helper">
                                Nome comercial da sua coleção, uso externo.
                            </small>
                        </div>

                        <div class="collection-editor__field">
                            <div class="collection-editor__label-row">
                                <label class="collection-editor__label" for="new-collection-description">
                                    Descrição da coleção
                                </label>
                                <v-tooltip text="Campo obrigatório" location="top">
                                    <template v-slot:activator="{ props }">
                                        <fa
                                            v-bind="props"
                                            class="collection-editor__help"
                                            icon="fa-solid fa-circle-exclamation"
                                        />
                                    </template>
                                </v-tooltip>
                            </div>
                            <textarea
                                id="new-collection-description"
                                class="collection-editor__textarea"
                                v-model="description"
                                maxlength="300"
                                placeholder="Descrição da coleção"
                            ></textarea>
                            <small class="collection-editor__counter">
                                {{ descriptionCount }}/300 caracteres
                            </small>
                        </div>

                        <div class="collection-editor__field">
                            <div class="collection-editor__label-row">
                                <label class="collection-editor__label" for="new-collection-status">
                                    Status da coleção
                                </label>
                            </div>
                            <small class="collection-editor__helper collection-editor__helper--tight">
                                Define se a coleção está ativa ou inativa.
                            </small>
                            <div
                                class="collection-editor__select-wrap"
                                :class="{ 'is-active': isActiveStatus }"
                            >
                                <span
                                    v-if="isActiveStatus"
                                    class="collection-editor__status-dot"
                                    aria-hidden="true"
                                ></span>
                                <select
                                    id="new-collection-status"
                                    v-model="status"
                                    class="collection-editor__select"
                                >
                                    <option value="1">Ativo</option>
                                    <option value="0">Inativo</option>
                                </select>
                            </div>
                        </div>
                    </section>

                    <section class="collection-editor__card collection-editor__card--sale">
                        <div class="collection-editor__card-header">
                            <span class="collection-editor__card-icon" aria-hidden="true">
                                <fa icon="fa-cart-shopping" />
                            </span>
                            <div>
                                <h2 class="collection-editor__card-title" id="new-collection-venda-type-label">
                                    Tipo de venda
                                </h2>
                                <p class="collection-editor__card-description">
                                    Escolha como essa coleção será disponibilizada para os alunos.
                                </p>
                            </div>
                        </div>

                        <div
                            class="collection-editor__sale-options"
                            role="radiogroup"
                            aria-labelledby="new-collection-venda-type-label"
                        >
                            <div
                                class="collection-editor__sale-option"
                                :class="{ 'collection-editor__sale-option--selected': !vendaAvulsa }"
                                @click="vendaAvulsa = false"
                            >
                                <input
                                    type="radio"
                                    v-model="vendaAvulsa"
                                    :value="false"
                                    id="new-collection-venda-assinatura"
                                />
                                <span class="collection-editor__radio" aria-hidden="true"></span>
                                <label for="new-collection-venda-assinatura">
                                    <span class="collection-editor__sale-head">
                                        <fa icon="fa-users" />
                                        <span class="collection-editor__sale-title">Assinatura</span>
                                    </span>
                                    <span class="collection-editor__sale-desc">
                                        Acesso através de plano mensal, semestral ou anual ou seu plano personalizado.
                                    </span>
                                </label>
                            </div>
                            <div
                                class="collection-editor__sale-option"
                                :class="{ 'collection-editor__sale-option--selected': vendaAvulsa }"
                                @click="vendaAvulsa = true"
                            >
                                <input
                                    type="radio"
                                    v-model="vendaAvulsa"
                                    :value="true"
                                    id="new-collection-venda-avulsa"
                                />
                                <span class="collection-editor__radio" aria-hidden="true"></span>
                                <label for="new-collection-venda-avulsa">
                                    <span class="collection-editor__sale-head">
                                        <fa icon="fa-cart-shopping" />
                                        <span class="collection-editor__sale-title">Venda avulsa</span>
                                    </span>
                                    <span class="collection-editor__sale-desc">
                                        Compra única com acesso temporário.
                                    </span>
                                </label>
                            </div>
                        </div>
                    </section>

                    <section class="collection-editor__card collection-editor__card--price">
                        <div class="collection-editor__card-header">
                            <span class="collection-editor__card-icon" aria-hidden="true">
                                <fa icon="fa-tag" />
                            </span>
                            <div>
                                <h2 class="collection-editor__card-title">Preço e disponibilidade</h2>
                                <p class="collection-editor__card-description">
                                    Configure o valor, tempo de acesso e opções de pagamento.
                                </p>
                            </div>
                        </div>

                        <div v-if="!vendaAvulsa" class="collection-editor__notice">
                            <h4>Modelo de Assinatura</h4>
                            <p>
                                No modelo de assinatura, o conteúdo do curso será disponibilizado junto aos demais em um modelo de assinatura.
                            </p>
                        </div>

                        <div v-else class="collection-editor__price-grid">
                            <div class="collection-editor__field">
                                <label class="collection-editor__label" for="new-collection-price">Preço (R$)</label>
                                <div class="collection-editor__price-field">
                                    <span class="collection-editor__currency">R$</span>
                                    <input
                                        id="new-collection-price"
                                        type="number"
                                        class="collection-editor__input"
                                        v-model="preco"
                                        placeholder="0.00"
                                        step="0.01"
                                    />
                                </div>
                            </div>
                            <div class="collection-editor__field">
                                <label class="collection-editor__label" for="new-collection-days">
                                    Tempo de disponibilidade
                                </label>
                                <div class="collection-editor__select-wrap">
                                    <select
                                        id="new-collection-days"
                                        v-model="tempoDisponibilidade"
                                        class="collection-editor__select"
                                    >
                                        <option value="30">30 dias</option>
                                        <option value="60">60 dias</option>
                                        <option value="90">90 dias</option>
                                        <option value="180">180 dias</option>
                                        <option value="365">365 dias</option>
                                    </select>
                                </div>
                            </div>
                            <div class="collection-editor__field">
                                <label class="collection-editor__label" for="new-collection-installments">
                                    Número de parcelas
                                </label>
                                <div class="collection-editor__select-wrap">
                                    <select
                                        id="new-collection-installments"
                                        v-model="numeroParcelas"
                                        class="collection-editor__select"
                                    >
                                        <option value="1">1x</option>
                                        <option value="2">2x</option>
                                        <option value="3">3x</option>
                                        <option value="4">4x</option>
                                        <option value="5">5x</option>
                                        <option value="6">6x</option>
                                        <option value="7">7x</option>
                                        <option value="8">8x</option>
                                        <option value="9">9x</option>
                                        <option value="10">10x</option>
                                        <option value="11">11x</option>
                                        <option value="12">12x</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <aside class="collection-editor__column">
                    <section class="collection-editor__card collection-editor__card--cover">
                        <div class="collection-editor__card-header">
                            <span class="collection-editor__card-icon" aria-hidden="true">
                                <fa icon="fa-image" />
                            </span>
                            <div>
                                <h2 class="collection-editor__card-title">Capa da coleção</h2>
                                <p class="collection-editor__card-description">
                                    Esta imagem será exibida para os alunos na vitrine da coleção.
                                </p>
                            </div>
                        </div>

                        <div class="collection-editor__cover-frame">
                            <img
                                v-if="imagePreviewCover"
                                class="collection-editor__cover-preview"
                                :src="imagePreviewCover"
                                alt="Capa da coleção"
                            />
                            <span v-else class="collection-editor__cover-empty">Nenhuma capa enviada</span>
                        </div>

                        <label
                            class="collection-editor__upload"
                            @dragover.prevent
                            @drop.prevent="onDropCover"
                        >
                            <input
                                class="collection-editor__file"
                                type="file"
                                @change="updateFileCover"
                                accept=".png, .jpg, .jpeg, .webp"
                            />
                            <span class="collection-editor__upload-icon" aria-hidden="true">
                                <fa v-if="loaderImage" icon="fa-cloud-arrow-up" />
                                <LoaderImage :hasReady="loaderImage" />
                            </span>
                            <span class="collection-editor__upload-title">Trocar imagem da capa</span>
                            <span class="collection-editor__upload-text">
                                Clique para enviar ou arraste um arquivo aqui
                            </span>
                            <span class="collection-editor__upload-hint">
                                PNG ou JPG • 1920 × 200 px (recomendado)
                            </span>
                        </label>
                    </section>

                    <section class="collection-editor__card collection-editor__card--summary">
                        <div class="collection-editor__card-header">
                            <span class="collection-editor__card-icon" aria-hidden="true">
                                <fa icon="fa-clipboard-list" />
                            </span>
                            <div>
                                <h2 class="collection-editor__card-title">Resumo da coleção</h2>
                                <p class="collection-editor__card-description">
                                    Visualize um resumo das configurações.
                                </p>
                            </div>
                        </div>

                        <div class="collection-editor__summary-row">
                            <span class="collection-editor__summary-icon"><fa icon="fa-file-lines" /></span>
                            <span class="collection-editor__summary-label">Nome</span>
                            <span class="collection-editor__summary-value">{{ summaryName }}</span>
                        </div>
                        <div class="collection-editor__summary-row">
                            <span class="collection-editor__summary-icon"><fa icon="fa-circle" /></span>
                            <span class="collection-editor__summary-label">Status</span>
                            <span class="collection-editor__summary-value">
                                <span
                                    v-if="summaryStatusLabel !== 'Não informado'"
                                    class="collection-editor__badge"
                                    :class="isActiveStatus ? 'collection-editor__badge--active' : 'collection-editor__badge--inactive'"
                                >
                                    {{ summaryStatusLabel }}
                                </span>
                                <template v-else>Não informado</template>
                            </span>
                        </div>
                        <div class="collection-editor__summary-row">
                            <span class="collection-editor__summary-icon"><fa icon="fa-cart-shopping" /></span>
                            <span class="collection-editor__summary-label">Tipo de venda</span>
                            <span class="collection-editor__summary-value">{{ summarySaleType }}</span>
                        </div>
                        <div class="collection-editor__summary-row">
                            <span class="collection-editor__summary-icon"><fa icon="fa-tag" /></span>
                            <span class="collection-editor__summary-label">Preço</span>
                            <span class="collection-editor__summary-value">{{ summaryPrice }}</span>
                        </div>
                        <div class="collection-editor__summary-row">
                            <span class="collection-editor__summary-icon"><fa icon="fa-clock" /></span>
                            <span class="collection-editor__summary-label">Tempo de disponibilidade</span>
                            <span class="collection-editor__summary-value">{{ summaryAvailability }}</span>
                        </div>
                        <div class="collection-editor__summary-row">
                            <span class="collection-editor__summary-icon"><fa icon="fa-credit-card" /></span>
                            <span class="collection-editor__summary-label">Parcelas</span>
                            <span class="collection-editor__summary-value">{{ summaryInstallments }}</span>
                        </div>
                    </section>
                </aside>
            </div>
        </main>

        <footer class="collection-editor__footer">
            <div class="collection-editor__footer-inner">
                <button
                    type="button"
                    class="collection-editor__btn collection-editor__btn--back"
                    @click="$router.push('/admin/colecoes')"
                >
                    <fa icon="fa-arrow-left" />
                    Voltar
                </button>
                <button
                    type="button"
                    class="collection-editor__btn collection-editor__btn--save"
                    :disabled="!loaderImage"
                    @click="newCollection"
                >
                    <fa icon="fa-floppy-disk" />
                    Criar coleção
                </button>
            </div>
        </footer>
    </div>
</template>


<script>
    import { mapState, mapActions } from 'vuex';
    import apiCollections from '@/utils/Weflix/collections';
    import LoaderImage from '@/components/Weflix/LoaderImage';
    import apiAWS from '@/utils/storage/aws';
    import { toast } from 'vue3-toastify';
    export default {
        data() {
            return {
                name: '',
                description: '',
                coverVideo: '',
                imagePreviewCover: '',
                loaderImage: true,
                vendaAvulsa: false,
                preco: '',
                numeroParcelas: '1',
                tempoDisponibilidade: '1',
                imageCover2: '',
                imagePreviewCover2: '',
                loaderImage2: true,
                status: 1,
                fillingPreview: false,
            };
        },
        computed: {
            ...mapState(['token']),
            isActiveStatus() {
                return String(this.status) === '1';
            },
            descriptionCount() {
                return (this.description || '').length;
            },
            summaryName() {
                const name = (this.name || '').trim();
                return name || 'Não informado';
            },
            summaryStatusLabel() {
                if (this.status === '' || this.status == null) return 'Não informado';
                return this.isActiveStatus ? 'Ativo' : 'Inativo';
            },
            summarySaleType() {
                return this.vendaAvulsa ? 'Venda avulsa' : 'Assinatura';
            },
            summaryPrice() {
                if (!this.vendaAvulsa) return 'Não informado';
                return this.formatCurrency(this.preco);
            },
            summaryAvailability() {
                if (!this.vendaAvulsa) return 'Não informado';
                return this.availabilityLabel(this.tempoDisponibilidade);
            },
            summaryInstallments() {
                if (
                    !this.vendaAvulsa
                    || this.numeroParcelas === ''
                    || this.numeroParcelas == null
                ) {
                    return 'Não informado';
                }
                return `${this.numeroParcelas}x`;
            },
        },
        components: {
            LoaderImage,
        },
        methods: {
            ...mapActions(['storeCurrentCollections']),

            deleteVideo(id, name) {
                this.deleteItem = true;
                this.nameItem = name;
            },
            closeDialog() {
                this.deleteItem = false;
            },
            async newCollection() {
                const payload = {
                    comercialName: this.name,
                    name: this.name,
                    status: this.status,
                    shortDescription: this.description,
                    imagePath: this.coverVideo,
                    vendaAvulsa: this.vendaAvulsa,
                    price: this.vendaAvulsa ? this.preco : null,
                    installment: this.vendaAvulsa ? this.numeroParcelas : null,
                    dayExpired: this.vendaAvulsa
                        ? this.tempoDisponibilidade
                        : null,
                    url: this.name
                        .toLowerCase()
                        .replace(/\s+/g, '-')
                        .replace(/[ç]/g, 'c')
                        .replace(/[^\w-]/g, ''),
                    imagePath2: this.imageCover2,
                };
                console.log(payload);
                try {
                    const response = await apiCollections.newCollection(
                        this.token,
                        payload
                    );
                    this.storeCurrentCollections({
                        currentCollections: response,
                    });
                    const id = response.id;
                    this.$router.push('/admin/colecao/' + id);
                } catch (error) {
                    console.error('Erro ao criar Coleção:', error);
                }
            },
            updateContentLongDescription(value) {
                this.description = value;
            },
            async putFileAws(url, file) {
                try {
                    const response = await apiAWS.putFileAWS(url, file);
                } catch (error) {
                    console.error('Erro ao enviar arquivo para AWS');
                }
            },
            onDropCover(event) {
                const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
                if (!file) return;
                this.updateFileCover({ target: { files: [file] } });
            },
            async updateFileCover(event) {
                this.loaderImage = false;
                const file = event.target.files[0];
                this.imagePreviewCover = URL.createObjectURL(file);
                try {
                    const response = await apiAWS.getSignatureImage(
                        this.token,
                        file.name
                    );
                    this.putFileAws(response.url, file);
                    this.coverVideo = response.token;
                    this.loaderImage = true;
                } catch (error) {
                    console.error('Erro na geração do TOKEN AWS');
                }
            },
            async updateFileCover2(event) {
                this.loaderImage2 = false;
                const file = event.target.files[0];
                this.imagePreviewCover2 = URL.createObjectURL(file);
                try {
                    const response = await apiAWS.getSignatureImage(
                        this.token,
                        file.name
                    );
                    this.putFileAws(response.url, file);
                    this.imageCover2 = response.token;
                    this.loaderImage2 = true;
                } catch (error) {
                    console.error('Erro na geração do TOKEN AWS');
                }
            },
            formatCurrency(value) {
                if (value === '' || value == null) return 'Não informado';
                let num = value;
                if (typeof value !== 'number') {
                    const raw = String(value).trim();
                    num = raw.includes(',')
                        ? parseFloat(raw.replace(/\./g, '').replace(',', '.'))
                        : parseFloat(raw);
                }
                if (!Number.isFinite(num)) return 'Não informado';
                return num.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                });
            },
            availabilityLabel(value) {
                const map = {
                    30: '30 dias',
                    60: '60 dias',
                    90: '90 dias',
                    180: '180 dias',
                    360: '360 dias',
                    365: '365 dias',
                };
                if (value === '' || value == null || value === 1 || value === '1') {
                    return 'Não informado';
                }
                return map[value] || map[Number(value)] || `${value} dias`;
            },
            notify(message, type = 'info') {
                toast(message, {
                    autoClose: 3000,
                    position: 'top-right',
                    theme: 'light',
                    limit: 1,
                    type: type,
                    dangerouslyHTMLString: true,
                });
            },
            isBlank(value) {
                return value == null || String(value).trim() === '';
            },
            createDemoImageFile(label, width, height, filename) {
                return new Promise((resolve, reject) => {
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Canvas não disponível'));
                        return;
                    }

                    const gradient = ctx.createLinearGradient(0, 0, width, height);
                    gradient.addColorStop(0, '#0f172a');
                    gradient.addColorStop(0.55, '#250891');
                    gradient.addColorStop(1, '#1ac7d0');
                    ctx.fillStyle = gradient;
                    ctx.fillRect(0, 0, width, height);

                    ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
                    ctx.font = `600 ${Math.max(28, Math.round(width / 22))}px sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(String(label).slice(0, 32), width / 2, height / 2);

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Falha ao gerar imagem'));
                            return;
                        }
                        resolve(
                            new File([blob], filename, {
                                type: 'image/png',
                                lastModified: Date.now(),
                            }),
                        );
                    }, 'image/png');
                });
            },
            async uploadCoverFile(file) {
                this.loaderImage = false;
                if (this.imagePreviewCover && String(this.imagePreviewCover).startsWith('blob:')) {
                    URL.revokeObjectURL(this.imagePreviewCover);
                }
                this.imagePreviewCover = URL.createObjectURL(file);
                try {
                    const response = await apiAWS.getSignatureImage(
                        this.token,
                        file.name,
                    );
                    await this.putFileAws(response.url, file);
                    this.coverVideo = response.token;
                    this.loaderImage = true;
                } catch (error) {
                    this.loaderImage = true;
                    console.error('Erro na geração do TOKEN AWS', error);
                    throw error;
                }
            },
            async fillPreviewData() {
                if (this.fillingPreview) return;
                this.fillingPreview = true;
                try {
                    const stamp = Date.now().toString().slice(-5);
                    const name = `Coleção demonstração ${stamp}`;

                    if (this.isBlank(this.name)) this.name = name;
                    if (this.isBlank(this.description)) {
                        this.description =
                            'Coleção introdutória com vídeos práticos para acelerar o aprendizado e organizar o conteúdo da sua loja.';
                    }
                    if (this.status == null || this.status === '') this.status = 1;

                    if (!this.vendaAvulsa) {
                        this.vendaAvulsa = true;
                    }
                    if (this.isBlank(this.preco)) this.preco = '97.90';
                    if (this.isBlank(this.numeroParcelas) || this.numeroParcelas === '1') {
                        this.numeroParcelas = '3';
                    }
                    if (
                        this.isBlank(this.tempoDisponibilidade)
                        || this.tempoDisponibilidade === '1'
                    ) {
                        this.tempoDisponibilidade = '90';
                    }

                    if (!this.coverVideo && !this.imagePreviewCover) {
                        const coverFile = await this.createDemoImageFile(
                            this.name,
                            1920,
                            200,
                            `colecao-capa-${stamp}.png`,
                        );
                        await this.uploadCoverFile(coverFile);
                    }

                    this.notify('Dados de demonstração preenchidos.', 'success');
                } catch (error) {
                    console.error('Erro ao preencher dados da coleção:', error);
                    this.notify('Não foi possível gerar a capa de demonstração.', 'error');
                } finally {
                    this.fillingPreview = false;
                }
            },
        },
    };
</script>

<style>
    @import url('@/css/weflix/collection-editor.css');
</style>
